/**
 * Email domain validation utility
 * Validates email domains for educator role registration
 */

import { ROLES } from '@/config/roles'

/**
 * List of blocked personal email domains for educators
 * These domains are not allowed for SCHOOL_ADMIN role
 */
const BLOCKED_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com'
]

/**
 * Extracts the domain from an email address
 * @param {string} email - The email address
 * @returns {string|null} - The domain or null if invalid
 */
const extractDomain = (email) => {
  if (!email || typeof email !== 'string') return null

  const parts = email.trim().toLowerCase().split('@')
  if (parts.length !== 2) return null

  return parts[1]
}

/**
 * Checks if a domain is blocked (including subdomains)
 * @param {string} domain - The domain to check
 * @returns {boolean} - True if domain is blocked
 */
const isDomainBlocked = (domain) => {
  if (!domain) return false

  // Direct match
  if (BLOCKED_DOMAINS.includes(domain)) {
    return true
  }

  // Check if it's a subdomain of a blocked domain
  // e.g., mail.gmail.com should be blocked
  return BLOCKED_DOMAINS.some((blockedDomain) => {
    return domain.endsWith(`.${blockedDomain}`)
  })
}

/**
 * Validates email domain for educator role
 * @param {string} email - The email address to validate
 * @param {string} role - The user role (ROLES.SCHOOL_ADMIN, ROLES.PARENT, etc.)
 * @returns {Object} - Validation result with isValid and error message
 */
export const validateEducatorEmail = (email, role) => {
  // Only validate for SCHOOL_ADMIN role
  if (role !== ROLES.SCHOOL_ADMIN) {
    return {
      isValid: true,
      error: null
    }
  }

  // Check if email is empty or invalid format
  if (!email || email.trim() === '') {
    return {
      isValid: false,
      error: 'Email is required'
    }
  }

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email.trim())) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    }
  }

  // Extract and validate domain
  const domain = extractDomain(email)

  if (!domain) {
    return {
      isValid: false,
      error: 'Invalid email format'
    }
  }

  // Check if domain is blocked
  if (isDomainBlocked(domain)) {
    return {
      isValid: false,
      error: 'Please use your school or district email address'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * Gets the list of blocked domains (for display purposes)
 * @returns {Array<string>} - Array of blocked domain names
 */
export const getBlockedDomains = () => {
  return [...BLOCKED_DOMAINS]
}
